import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface CookLog {
  date: string; // ISO timestamp of when cooking was started
  rated: boolean; // true once the user has rated this cook session
}

/**
 * One person's verdict on a recipe they actually ate.
 *
 * Three values rather than five stars because the people being asked include
 * children, dictated by a parent — "did you like it?" has three honest
 * answers, and asking a five-year-old to distinguish 3 from 4 invents
 * precision that was never there.
 */
export type VerdictValue = 'love' | 'ok' | 'no';

export interface EaterVerdict {
  value: VerdictValue;
  at: string; // ISO
}

/** Score each verdict contributes to the derived 1-5 rating. */
const VERDICT_SCORE: Record<VerdictValue, number> = {
  love: 5,
  ok: 3,
  no: 1,
};

/**
 * Collapse a household's verdicts into the single 1-5 scalar the rest of the
 * app already reads (`selectTopRatedRecipes`, `CookedLine`, `CookedStamp`, the
 * taste profile).
 *
 * Returns undefined when there is nothing to derive from, which the callers
 * treat as "leave whatever rating was there alone" — a recipe rated by hand
 * before this feature existed keeps its rating until someone actually answers
 * for the table.
 */
export const deriveRatingFromVerdicts = (
  verdicts: Record<string, EaterVerdict> | undefined
): number | undefined => {
  const values = Object.values(verdicts ?? {});
  if (values.length === 0) return undefined;
  const total = values.reduce((sum, v) => sum + VERDICT_SCORE[v.value], 0);
  return Math.max(1, Math.min(5, Math.round(total / values.length)));
};

/**
 * How long a locally-set verdict is assumed to still be in flight, when
 * nothing better is known.
 *
 * "The cloud does not have this answer" is ambiguous: either the write has not
 * landed yet, or someone retracted it and the key was deleted. This clock was
 * the original way to disambiguate, and it is wrong in the case that matters:
 * a write that FAILED is indistinguishable from a retraction after 15 seconds,
 * so the snapshot quietly erased the answer from the device that gave it. That
 * is what "I rated it on my phone and later nothing was rated" actually was.
 *
 * The outbox (`pending`) answers the question properly — a field still awaiting
 * acknowledgement is in flight no matter how long it has been — so this is now
 * only the fallback for callers that supply no outbox entry (the merge unit
 * tests, and any pre-outbox persisted state).
 */
const VERDICT_IN_FLIGHT_MS = 15_000;

/**
 * One recipe's unacknowledged writes.
 *
 * Firestore had exactly one chance at every rating: an 800ms debounce, a single
 * `setDoc`, and a `catch` that logged. Hide the tab inside that window — lock
 * the phone right after tapping, which is precisely when people rate a meal —
 * and the write never happened. The device kept showing the answer, so nothing
 * looked wrong until you opened the app somewhere else.
 *
 * This is the record of what has not been confirmed written. It lives in the
 * slice rather than in the middleware's module scope because the slice is in
 * the redux-persist whitelist: an interrupted write survives the reload that
 * interrupted it, and gets retried on the next boot.
 */
export interface PendingSync {
  /** Fields whose current local value has not been acknowledged by the cloud. */
  fields: string[];
  /**
   * Eaters whose verdict was retracted locally but not yet deleted in the
   * cloud. A merge write cannot remove a map key, so these need their own
   * `deleteField` call — and if that call is lost, the next snapshot brings
   * the retracted answer straight back.
   */
  clearedEaters?: string[];
}

/**
 * Reconcile one recipe's local entry with the cloud's.
 *
 * Cloud-wins-wholesale was fine while metadata only arrived once per session.
 * With a live listener it runs on every partner write, and `verdicts` cannot
 * survive that: it is a per-person map, and two people answer within a second
 * of each other (the household's own data shows exactly that), so a snapshot
 * generated mid-burst must not revert the answer that has not round-tripped
 * yet. Merged per eaterId, newest `at` wins.
 *
 * `rating` is derived, so it is recomputed from the merged verdicts rather than
 * copied from either side — otherwise a stale scalar can outrank fresh answers.
 */
export const mergeMetadataEntry = (
  local: RecipeMetadata | undefined,
  cloud: RecipeMetadata,
  now: number = Date.now(),
  pending?: PendingSync
): RecipeMetadata => {
  if (!local) return cloud;

  const verdicts = mergeVerdicts(local.verdicts, cloud.verdicts, now, pending);
  const cookLog = mergeCookLogs(local.cookLog, cloud.cookLog);

  // Cloud wins for every plain scalar, matching the previous behaviour —
  // EXCEPT for a field still sitting in the outbox. That field's local value
  // is a user action the cloud has not been told about yet, so letting a
  // snapshot win would erase the change and then stop retrying it.
  const merged: RecipeMetadata = { ...local, ...cloud };
  for (const field of pending?.fields ?? []) {
    if (field === 'verdicts' || field === 'cookLog') continue; // merged below
    const mine = (local as unknown as Record<string, unknown>)[field];
    if (mine !== undefined) {
      (merged as unknown as Record<string, unknown>)[field] = mine;
    }
  }

  if (verdicts) merged.verdicts = verdicts;
  else delete merged.verdicts;

  if (cookLog) merged.cookLog = cookLog;

  const derived = deriveRatingFromVerdicts(verdicts);
  if (derived !== undefined) {
    merged.rating = derived;
  } else if (cloud.rating === undefined && local.rating !== undefined) {
    // Nothing to derive from and the cloud has no opinion — keep ours rather
    // than blanking a rating the user can see.
    merged.rating = local.rating;
  }

  return merged;
};

const mergeVerdicts = (
  local: Record<string, EaterVerdict> | undefined,
  cloud: Record<string, EaterVerdict> | undefined,
  now: number,
  pending?: PendingSync
): Record<string, EaterVerdict> | undefined => {
  if (!local && !cloud) return undefined;
  const out: Record<string, EaterVerdict> = { ...(cloud ?? {}) };
  const verdictsPending = pending?.fields.includes('verdicts') ?? false;
  const clearedEaters = pending?.clearedEaters ?? [];

  for (const [eaterId, mine] of Object.entries(local ?? {})) {
    const theirs = out[eaterId];
    if (theirs) {
      // Ties go to the cloud: an equal timestamp means our own write came back.
      if (mine.at > theirs.at) out[eaterId] = mine;
      continue;
    }
    // Absent from the cloud. If the write is still in the outbox it has not
    // been told yet — keep it however old it is, because the retry will land
    // it. Otherwise fall back to the clock.
    if (verdictsPending) {
      out[eaterId] = mine;
      continue;
    }
    const age = now - Date.parse(mine.at);
    if (Number.isNaN(age) || age < VERDICT_IN_FLIGHT_MS) out[eaterId] = mine;
  }

  // A retraction the cloud has not accepted yet must not be resurrected by the
  // snapshot that still carries it.
  for (const eaterId of clearedEaters) delete out[eaterId];

  return Object.keys(out).length > 0 ? out : undefined;
};

const mergeCookLogs = (
  local: CookLog[] | undefined,
  cloud: CookLog[] | undefined
): CookLog[] | undefined => {
  if (!local && !cloud) return undefined;
  const byDate = new Map<string, CookLog>();
  for (const entry of [...(cloud ?? []), ...(local ?? [])]) {
    const existing = byDate.get(entry.date);
    // `rated: true` is a one-way door — never let a stale copy un-rate a cook.
    byDate.set(entry.date, {
      date: entry.date,
      rated: (existing?.rated ?? false) || entry.rated,
    });
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Recipe metadata tracks user-specific information about each recipe
 * such as how often it's been planned, when it was last made, ratings, notes, and favorites.
 */
export interface RecipeMetadata {
  recipeId: string;
  timesPlanned: number;
  lastPlanned?: string; // ISO date string
  lastMade?: string; // ISO date string
  // Attribution for cooked-stamp UI ("last cooked by X").
  // Populated only when household sharing is active.
  lastMadeBy?: { uid: string; displayName: string | null };
  lastViewed?: string; // ISO date string - when recipe detail was opened
  firstSeenAt?: string; // ISO date string - when recipe was first added to the collection
  /**
   * 1-5 stars. DERIVED from `verdicts` whenever any exist — see
   * deriveRatingFromVerdicts. Still writable directly via setRating for the
   * paths that predate per-eater verdicts.
   */
  rating?: number;
  /**
   * What each person at the table thought, keyed by eaterId. The two account
   * holders are keyed by their uid; everyone else by their generated eater id.
   *
   * A map rather than an array so two offline writes merge per key instead of
   * clobbering each other — the same reasoning applied to PlannedMeal.approvals.
   */
  verdicts?: Record<string, EaterVerdict>;
  notes?: string;
  hasNotes: boolean;
  isFavorite?: boolean; // Bookmarked/saved recipe
  cookLog?: CookLog[]; // History of cook sessions
}

export interface RecipeMetadataState {
  metadata: Record<string, RecipeMetadata>;
  /**
   * The outbox: recipeId → what has not been confirmed written. Persisted with
   * the rest of the slice, so a write interrupted by a tab close is retried on
   * the next boot instead of being lost.
   *
   * Optional because it has to be: redux-persist hands back the slice exactly
   * as it was stored, and every install that predates the outbox stored a
   * slice without one. Read it through `outbox()` or `selectPendingSync`.
   */
  pending?: Record<string, PendingSync>;
}

const initialState: RecipeMetadataState = {
  metadata: {},
  pending: {},
};

/**
 * Persisted state written before the outbox existed has no `pending` key, and
 * redux-persist hands the slice back as it was stored. Every reducer that
 * touches the outbox goes through here.
 */
const outbox = (state: RecipeMetadataState): Record<string, PendingSync> => {
  if (!state.pending) state.pending = {};
  return state.pending;
};

/**
 * Helper to get or create metadata for a recipe
 */
function getOrCreateMetadata(state: RecipeMetadataState, recipeId: string): RecipeMetadata {
  if (!state.metadata[recipeId]) {
    state.metadata[recipeId] = {
      recipeId,
      timesPlanned: 0,
      hasNotes: false,
      firstSeenAt: new Date().toISOString(),
    };
  }
  return state.metadata[recipeId];
}

const recipeMetadataSlice = createSlice({
  name: 'recipeMetadata',
  initialState,
  reducers: {
    /**
     * Record that a recipe was planned (increment counter and update lastPlanned)
     */
    recordPlanned: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.timesPlanned += 1;
      metadata.lastPlanned = new Date().toISOString();
    },

    /**
     * Record that a recipe was made (update lastMade date).
     * Optionally records who cooked it for household attribution UI.
     */
    recordMade: (
      state,
      action: PayloadAction<
        string | { recipeId: string; by?: { uid: string; displayName: string | null } }
      >
    ) => {
      const recipeId =
        typeof action.payload === 'string'
          ? action.payload
          : action.payload.recipeId;
      const by =
        typeof action.payload === 'string' ? undefined : action.payload.by;
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.lastMade = new Date().toISOString();
      if (by) {
        metadata.lastMadeBy = by;
      }
    },

    /**
     * Set the rating for a recipe (1-5 stars)
     */
    setRating: (state, action: PayloadAction<{ recipeId: string; rating: number }>) => {
      const { recipeId, rating } = action.payload;
      // Validate rating is 1-5
      const validRating = Math.max(1, Math.min(5, Math.round(rating)));
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.rating = validRating;
    },

    /**
     * Record (or clear) one person's verdict on a recipe, and recompute the
     * derived rating in the same pass.
     *
     * Passing value: null clears that person's verdict — the sheet uses it for
     * "tap the selected chip again to un-answer".
     *
     * Clearing the last verdict leaves `rating` at whatever it derived to. The
     * alternative (deleting it) would silently erase a recipe from "My top
     * rated" because someone mis-tapped, which is worse than a stale number.
     */
    setEaterVerdict: (
      state,
      action: PayloadAction<{
        recipeId: string;
        eaterId: string;
        value: VerdictValue | null;
      }>
    ) => {
      const { recipeId, eaterId, value } = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);

      if (value === null) {
        if (metadata.verdicts) delete metadata.verdicts[eaterId];
      } else {
        if (!metadata.verdicts) metadata.verdicts = {};
        metadata.verdicts[eaterId] = { value, at: new Date().toISOString() };
      }

      const derived = deriveRatingFromVerdicts(metadata.verdicts);
      if (derived !== undefined) {
        metadata.rating = derived;
      }
    },

    /**
     * Clear the rating for a recipe
     */
    clearRating: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      if (state.metadata[recipeId]) {
        delete state.metadata[recipeId].rating;
      }
    },

    /**
     * Set notes for a recipe
     */
    setNotes: (state, action: PayloadAction<{ recipeId: string; notes: string }>) => {
      const { recipeId, notes } = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.notes = notes;
      metadata.hasNotes = notes.trim().length > 0;
    },

    /**
     * Clear notes for a recipe
     */
    clearNotes: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      if (state.metadata[recipeId]) {
        delete state.metadata[recipeId].notes;
        state.metadata[recipeId].hasNotes = false;
      }
    },

    /**
     * Batch record multiple recipes as planned (used when generating a week)
     */
    recordMultiplePlanned: (state, action: PayloadAction<string[]>) => {
      const recipeIds = action.payload;
      const now = new Date().toISOString();
      recipeIds.forEach(recipeId => {
        const metadata = getOrCreateMetadata(state, recipeId);
        metadata.timesPlanned += 1;
        metadata.lastPlanned = now;
      });
    },

    /**
     * Toggle favorite status for a recipe
     */
    toggleFavorite: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.isFavorite = !metadata.isFavorite;
    },

    /**
     * Set favorite status for a recipe
     */
    setFavorite: (state, action: PayloadAction<{ recipeId: string; isFavorite: boolean }>) => {
      const { recipeId, isFavorite } = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.isFavorite = isFavorite;
    },

    /**
     * Record that a recipe was viewed (update lastViewed date)
     */
    recordViewed: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);
      metadata.lastViewed = new Date().toISOString();
    },

    /**
     * Record a standalone cook session (cooking mode, not meal-plan based).
     * Appends a new CookLog entry and updates lastMade.
     */
    recordCooked: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      const metadata = getOrCreateMetadata(state, recipeId);
      const entry: CookLog = { date: new Date().toISOString(), rated: false };
      if (!metadata.cookLog) {
        metadata.cookLog = [];
      }
      metadata.cookLog.push(entry);
      metadata.lastMade = entry.date;
    },

    /**
     * Mark the most recent unrated cook session as rated.
     */
    markCookRated: (state, action: PayloadAction<string>) => {
      const recipeId = action.payload;
      const cookLog = state.metadata[recipeId]?.cookLog;
      if (!cookLog) return;
      const pending = cookLog.slice().reverse().find(e => !e.rated);
      if (pending) {
        pending.rated = true;
      }
    },

    /**
     * Replace the entire metadata map (used after cloud hydration on sign-in
     * or after pairing into a household).
     */
    /**
     * Fold a cloud snapshot into local state.
     *
     * Used by BOTH the one-shot download at sign-in and the live listener, so
     * it has to be safe to run on every partner write — see mergeMetadataEntry
     * for why a plain cloud-wins spread is not. Local-only entries survive
     * (pre-auth guest activity).
     *
     * Deliberately NOT in recipeMetadataSyncMiddleware's matcher: that omission
     * is what stops write -> snapshot -> write from looping.
     */
    hydrateMetadataFromCloud: (
      state,
      action: PayloadAction<Record<string, RecipeMetadata>>
    ) => {
      const pending = outbox(state);
      for (const [recipeId, cloud] of Object.entries(action.payload)) {
        state.metadata[recipeId] = mergeMetadataEntry(
          state.metadata[recipeId],
          cloud,
          Date.now(),
          pending[recipeId]
        );
      }
    },

    /**
     * Record that a recipe's local state has changed and the cloud has not
     * acknowledged it yet.
     *
     * Dispatched by the sync middleware BEFORE any of its own guards, so a tap
     * that happens while Firebase auth is still resolving is queued rather
     * than dropped on the floor.
     */
    markSyncPending: (
      state,
      action: PayloadAction<{
        recipeId: string;
        fields?: string[];
        clearedEater?: string;
      }>
    ) => {
      const { recipeId, fields = [], clearedEater } = action.payload;
      const pending = outbox(state);
      const entry = pending[recipeId] ?? { fields: [] };

      entry.fields = [...new Set([...entry.fields, ...fields])];

      if (clearedEater) {
        entry.clearedEaters = [
          ...new Set([...(entry.clearedEaters ?? []), clearedEater]),
        ];
      } else if (entry.clearedEaters?.length && fields.includes('verdicts')) {
        // Answering again after retracting supersedes the retraction — sending
        // both would race a delete against a set for the same key.
        entry.clearedEaters = undefined;
      }

      pending[recipeId] = entry;
    },

    /**
     * The cloud confirmed the write. Anything the user changed since is still
     * pending, so only the fields that actually went out are cleared.
     */
    markSyncAcked: (
      state,
      action: PayloadAction<{
        recipeId: string;
        fields?: string[];
        clearedEaters?: string[];
      }>
    ) => {
      const { recipeId, fields = [], clearedEaters = [] } = action.payload;
      const pending = outbox(state);
      const entry = pending[recipeId];
      if (!entry) return;

      entry.fields = entry.fields.filter((f) => !fields.includes(f));
      if (entry.clearedEaters) {
        entry.clearedEaters = entry.clearedEaters.filter(
          (e) => !clearedEaters.includes(e)
        );
        if (entry.clearedEaters.length === 0) entry.clearedEaters = undefined;
      }

      if (entry.fields.length === 0 && !entry.clearedEaters?.length) {
        delete pending[recipeId];
      }
    },
  },
});

/**
 * Pull all recipe metadata from Firestore under the active scope and merge
 * into local state. Cloud wins for collisions.
 */
export const downloadRecipeMetadataFromCloud = createAsyncThunk(
  'recipeMetadata/downloadFromCloud',
  async (userId: string, { getState, dispatch, rejectWithValue }) => {
    try {
      // Lazy-import so the slice doesn't transitively pull in firebaseConfig
      // (which references react-native Platform) at module load — the test
      // environment doesn't always mock that.
      const { isFirebaseEnabled } = await import('../services/firebaseConfig');
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }
      const { fetchAllRecipeMetadataFromCloud } = await import(
        '../services/recipeMetadataSyncService'
      );
      const state = getState() as { auth?: { householdId?: string | null } };
      const householdId = state.auth?.householdId ?? null;
      const cloud = await fetchAllRecipeMetadataFromCloud(userId, householdId);
      dispatch(recipeMetadataSlice.actions.hydrateMetadataFromCloud(cloud));
      return { count: Object.keys(cloud).length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to download recipe metadata');
    }
  }
);

export const {
  recordPlanned,
  recordMade,
  setRating,
  setEaterVerdict,
  clearRating,
  setNotes,
  clearNotes,
  recordMultiplePlanned,
  toggleFavorite,
  setFavorite,
  recordViewed,
  hydrateMetadataFromCloud,
  recordCooked,
  markCookRated,
  markSyncPending,
  markSyncAcked,
} = recipeMetadataSlice.actions;

/**
 * What the DISK copy of this slice keeps when storage is short — the
 * low-storage shed for this slice (see storageShedding.ts). Pure: the store
 * is never trimmed, only what redux-persist writes.
 *
 * Two kinds of entry survive, because for both the disk is the only copy:
 *   - anything the outbox still owes the cloud — `pending` records field
 *     NAMES and the values live in the entry, so dropping the entry would
 *     leave the retry with nothing to send;
 *   - anything carrying cooking history. `recordCooked` / `markCookRated`
 *     are not in the sync middleware's matcher, so `cookLog` never reaches
 *     Firestore and cannot come back from it.
 * Everything else is re-seeded from Firestore on the next boot.
 */
export const trimMetadataForStorage = (state: RecipeMetadataState): RecipeMetadataState => {
  const pending = state.pending ?? {};
  const metadata: Record<string, RecipeMetadata> = {};
  for (const [recipeId, entry] of Object.entries(state.metadata ?? {})) {
    if (pending[recipeId] || entry.cookLog?.length) metadata[recipeId] = entry;
  }
  return { ...state, metadata, pending };
};

// ============== SELECTORS ==============

interface RootState {
  recipeMetadata: RecipeMetadataState;
}

const NO_PENDING: Record<string, PendingSync> = {};

/**
 * Everything still waiting to reach Firestore, recipeId → fields.
 *
 * Read by the retry pass on boot, on reconnect, and when the tab comes back to
 * the foreground.
 */
export const selectPendingSync = (
  state: RootState
): Record<string, PendingSync> => state.recipeMetadata.pending ?? NO_PENDING;

/**
 * Select metadata for a specific recipe
 */
export const selectRecipeMetadata = (state: RootState, recipeId: string): RecipeMetadata | undefined =>
  state.recipeMetadata.metadata[recipeId];

const NO_VERDICTS: Record<string, EaterVerdict> = {};

/**
 * What everyone at the table thought of this recipe, keyed by eaterId.
 *
 * Returns a shared frozen-in-practice empty object rather than a fresh {} so
 * useSelector does not re-render on every store tick for an unrated recipe.
 */
export const selectRecipeVerdicts = (
  state: RootState,
  recipeId: string
): Record<string, EaterVerdict> =>
  state.recipeMetadata.metadata[recipeId]?.verdicts ?? NO_VERDICTS;

/**
 * Select the full metadata map (recipeId → metadata)
 */
export const selectAllRecipeMetadata = (state: RootState): Record<string, RecipeMetadata> =>
  state.recipeMetadata.metadata;

/**
 * Select all recipes that have been rated
 */
export const selectAllRatedRecipes = (state: RootState): RecipeMetadata[] =>
  Object.values(state.recipeMetadata.metadata).filter(m => m.rating !== undefined);

/**
 * Select all recipes that have been made recently (within last 30 days)
 */
export const selectRecentlyMadeRecipes = (state: RootState): RecipeMetadata[] => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const cutoff = thirtyDaysAgo.toISOString();

  return Object.values(state.recipeMetadata.metadata).filter(
    m => m.lastMade && m.lastMade >= cutoff
  );
};

/**
 * Select all recipes with notes
 */
export const selectRecipesWithNotes = (state: RootState): RecipeMetadata[] =>
  Object.values(state.recipeMetadata.metadata).filter(m => m.hasNotes);

/**
 * Select recipes sorted by times planned (most planned first)
 */
export const selectMostPlannedRecipes = (state: RootState, limit: number = 10): RecipeMetadata[] =>
  Object.values(state.recipeMetadata.metadata)
    .sort((a, b) => b.timesPlanned - a.timesPlanned)
    .slice(0, limit);

/**
 * Select recipes sorted by rating (highest rated first)
 */
export const selectTopRatedRecipes = (state: RootState, limit: number = 10): RecipeMetadata[] =>
  Object.values(state.recipeMetadata.metadata)
    .filter(m => m.rating !== undefined)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit);

/**
 * Get the number of times a recipe has been planned
 */
export const selectTimesPlanned = (state: RootState, recipeId: string): number =>
  state.recipeMetadata.metadata[recipeId]?.timesPlanned || 0;

/**
 * Check if a recipe has been rated
 */
export const selectHasRating = (state: RootState, recipeId: string): boolean =>
  state.recipeMetadata.metadata[recipeId]?.rating !== undefined;

/**
 * Check if a recipe has notes
 */
export const selectHasNotes = (state: RootState, recipeId: string): boolean =>
  state.recipeMetadata.metadata[recipeId]?.hasNotes || false;

/**
 * Check if a recipe is favorited
 */
export const selectIsFavorite = (state: RootState, recipeId: string): boolean =>
  state.recipeMetadata.metadata[recipeId]?.isFavorite || false;

/**
 * Select all favorite recipes
 */
export const selectFavoriteRecipes = (state: RootState): RecipeMetadata[] =>
  Object.values(state.recipeMetadata.metadata).filter(m => m.isFavorite);

/**
 * Select all favorite recipe IDs
 */
export const selectFavoriteRecipeIds = (state: RootState): string[] =>
  Object.values(state.recipeMetadata.metadata)
    .filter(m => m.isFavorite)
    .map(m => m.recipeId);

/**
 * Select recently viewed recipes sorted by lastViewed (most recent first)
 */
export const selectRecentlyViewedRecipes = (state: RootState, limit: number = 10): RecipeMetadata[] =>
  Object.values(state.recipeMetadata.metadata)
    .filter(m => m.lastViewed !== undefined)
    .sort((a, b) => (b.lastViewed || '').localeCompare(a.lastViewed || ''))
    .slice(0, limit);

/**
 * Select recently viewed recipe IDs sorted by lastViewed (most recent first)
 */
export const selectRecentlyViewedRecipeIds = (state: RootState, limit: number = 10): string[] =>
  selectRecentlyViewedRecipes(state, limit).map(m => m.recipeId);

/**
 * Select the most recent cook log entry that hasn't been rated yet.
 * Used to show the "À déguster" pending-rating card in the recipe header.
 */
export const selectPendingRatingCookLog = (state: RootState, recipeId: string): CookLog | undefined => {
  const cookLog = state.recipeMetadata.metadata[recipeId]?.cookLog;
  if (!cookLog?.length) return undefined;
  return cookLog.slice().reverse().find(e => !e.rated);
};

export default recipeMetadataSlice.reducer;
